import type { useTranslation } from "@sagot/i18n";
import { capLabel } from "./builder/wait-model";

type TFunction = ReturnType<typeof useTranslation>["t"];

/** The variable in quotes that a custom body must contain, written out so the message can show it without i18next reading it as a placeholder. */
const BODY_EXAMPLE = '"{{visitor.name}}"';

/**
 * One thing wrong with the workflow, wherever it sits. The client finds them as the author edits (`graph-check.ts`,
 * mirroring the backend's `graph.py` and `plan_rules.py`); the server reports the rest when a save is refused.
 * `where` is the server's own location text ("paths.A.steps.0", "step send_webhook", "settings"): the same problem
 * has the same `code` and `where` on both sides, which is what `contracts/workflow-graph-cases.json` asserts.
 */
export interface Problem {
  /** A client rule (`unknown_path` ...), `incomplete` (a step still needs a field) or `server` (a line the server wrote). */
  code: string;
  where: string;
  /** The path it sits in (a path letter), when it sits in one. */
  path?: string;
  /** The step it sits in, when it sits in one. */
  stepId?: string;
  params: Readonly<Record<string, string | number>>;
  source: "client" | "server";
}

/**
 * A graph problem and the reason a step is "incomplete" that tell the same thing: the card says it once, in the words of
 * the reason ("Choose where each button leads"), and the count of problems counts it once.
 */
export const INCOMPLETE_REASON_OF_PROBLEM: Readonly<Record<string, string>> = { exit_unset: "buttonPaths", missing_target_hand: "person" };

/** Problems the server reports only when the workflow is published (a draft may use anything the plan lacks). */
export const PUBLISH_ONLY_CODES: ReadonlySet<string> = new Set(["needs_plan", "wait_too_long"]);

/** The `code` + `where` of a problem: its identity for comparisons and for merging two lists. */
export const problemKey = (problem: Pick<Problem, "code" | "where">): string => `${problem.code} ${problem.where}`;

const X = "engage.workflows.extras";

/** Parameters that hold a key or a number and are written out in the author's language: the plan, the part of a step, a wait limit. */
function readableParams(problem: Problem, t: TFunction): Problem["params"] {
  const { code, params } = problem;
  if (code === "needs_plan") return { ...params, plan: t(`console.plans.${String(params.plan)}`) };
  if (code === "translation_not_translatable") return { ...params, what: t(`${X}.problemParts.${String(params.what)}`) };
  if (code === "wait_too_long") {
    const { value, unit } = capLabel(Number(params.minutes));
    return { ...params, limit: t(`${X}.limit.${unit}`, { count: value }) };
  }
  if (code === "bad_template") return { ...params, example: BODY_EXAMPLE };
  return params;
}

/** A problem written for the author, without the location (the card it sits on says where). */
export function problemText(problem: Problem, t: TFunction): string {
  if (problem.code === "server") return String(problem.params.text ?? "");
  if (problem.code === "incomplete") return t(`engage.workflows.builder.incomplete.${String(problem.params.reason)}`);
  return t(`${X}.problems.${problem.code}`, readableParams(problem, t));
}

/** What the problems on one card are called ("2 problems"). */
export const problemCountLabel = (count: number, t: TFunction): string => t(`${X}.problemCount`, { count });

/** Problems grouped by the step and the path they sit in. */
export interface ProblemIndex {
  all: readonly Problem[];
  byStep: ReadonlyMap<string, readonly Problem[]>;
  byPath: ReadonlyMap<string, readonly Problem[]>;
}

const pushTo = (map: Map<string, Problem[]>, key: string, problem: Problem) => {
  const list = map.get(key) ?? [];
  list.push(problem);
  map.set(key, list);
};

export function indexProblems(problems: readonly Problem[]): ProblemIndex {
  const byStep = new Map<string, Problem[]>();
  const byPath = new Map<string, Problem[]>();
  for (const problem of problems) {
    if (problem.stepId) pushTo(byStep, problem.stepId, problem);
    if (problem.path) pushTo(byPath, problem.path, problem);
  }
  return { all: problems, byStep, byPath };
}

export const NO_PROBLEMS: ProblemIndex = indexProblems([]);
